import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mountain, ArrowRight, MapPin, BookOpen } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import Seo from "../components/Seo";
import { SITE_URL, BUSINESS } from "../lib/site";

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Guide alla Val di Sole — Appartamenti in Val di Sole",
    url: `${SITE_URL}/guide`,
    inLanguage: "it-IT",
    blogPost: posts.slice(0, 20).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE_URL}/guide/${p.slug}`,
      datePublished: p.published_at || p.created_at,
      description: p.excerpt || p.meta_description,
    })),
  };

  return (
    <div className="bg-ivory min-h-screen">
      <Seo
        title="Guide alla Val di Sole | Appartamenti in Val di Sole"
        description="Consigli, itinerari ed esperienze per la tua vacanza in Val di Sole: sci a Folgarida-Marilleva, rafting sul Noce, trekking nelle Dolomiti di Brenta e molto altro."
        path="/guide"
        jsonLd={jsonLd}
      />

      <header className="sticky top-0 z-30 bg-fir-deep/95 backdrop-blur text-ivory px-6 md:px-16 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-xl">Appartamenti in Val di Sole</span></Link>
        <Link to="/#strutture" className="gold-btn px-4 py-2 rounded-xl text-sm">Verifica disponibilità</Link>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10">
        <div className="flex items-center gap-2 text-gold text-sm uppercase tracking-wide mb-3"><BookOpen size={16} /> Guide & consigli locali</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-fir leading-tight">Guide alla Val di Sole</h1>
        <p className="text-fir-deep/70 mt-4 max-w-2xl leading-relaxed">Idee, itinerari e consigli pratici per vivere Dimaro, Folgarida-Marilleva e le Dolomiti di Brenta in ogni stagione — scritti pensando a chi vuole una vacanza autentica in Trentino.</p>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-8">
        <div className="max-w-3xl space-y-4 text-fir-deep/75 leading-relaxed">
          <p>
            La <strong>Val di Sole</strong> è una delle valli più belle del Trentino: si sviluppa lungo il
            fiume Noce, tra il Parco Nazionale dello Stelvio e il Parco Naturale Adamello-Brenta, con Dimaro
            come punto di partenza ideale per esplorarla. In queste guide raccogliamo tutto ciò che serve per
            organizzare al meglio il soggiorno, stagione per stagione, con informazioni pratiche su impianti,
            escursioni, eventi e ristoranti tipici.
          </p>
          <p>
            <strong>In inverno</strong> il comprensorio di Folgarida-Marilleva, collegato a Madonna di
            Campiglio nello Skirama Dolomiti, offre oltre 150 km di piste per ogni livello, oltre a ciaspolate
            e sci di fondo. <strong>In estate</strong> la valle diventa il regno delle attività all'aria
            aperta: rafting e canyoning sul Noce, percorsi in e-bike lungo la ciclabile, trekking verso i
            rifugi delle Dolomiti di Brenta e giornate di relax alle terme di Pejo e Rabbi.
          </p>
          <p>
            Le nostre guide nascono dall'esperienza diretta di chi vive e accoglie ospiti a Dimaro: consigli
            reali su cosa fare, dove mangiare e come muoversi, pensati per aiutarti a vivere la Val di Sole
            come un local. Scegli un tema qui sotto e inizia a pianificare la tua vacanza in Trentino.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-20">
        {loading ? (
          <p className="text-fir-deep/50">Caricamento…</p>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-3xl border border-stone p-10 text-center text-fir-deep/60" data-testid="blog-empty">
            Presto pubblicheremo le nostre guide alla Val di Sole. Torna a trovarci!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            {posts.map((p) => (
              <Link key={p.id} to={`/guide/${p.slug}`} data-testid="blog-card"
                className="bg-white rounded-3xl border border-stone overflow-hidden hover:shadow-xl hover:border-gold/40 transition-all group flex flex-col">
                {p.cover_image && <img src={resolveImg(p.cover_image)} alt={p.title} loading="lazy" className="h-44 w-full object-cover" />}
                <div className="p-7 flex flex-col flex-1">
                <div className="flex flex-wrap gap-2 mb-3">
                  {(p.keywords || []).slice(0, 2).map((k) => (
                    <span key={k} className="text-xs px-2.5 py-1 rounded-full bg-stone/60 text-fir-deep/70">{k}</span>
                  ))}
                </div>
                <h2 className="font-display text-2xl text-fir leading-snug group-hover:text-gold transition-colors">{p.title}</h2>
                <p className="text-fir-deep/70 mt-3 leading-relaxed flex-1">{p.excerpt || p.meta_description}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-gold font-medium text-sm">Leggi la guida <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <footer className="bg-fir-deep text-ivory px-6 py-10">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-2xl">Appartamenti in Val di Sole</span></div>
          <div className="text-ivory/60 text-sm flex items-center gap-2"><MapPin size={15} className="text-gold" /> {BUSINESS.locality}, Val di Sole (TN) · {BUSINESS.phone}</div>
        </div>
        <div className="max-w-5xl mx-auto mt-4 flex gap-4 text-sm">
          <Link to="/privacy" className="text-ivory/50 hover:text-gold">Privacy</Link>
          <Link to="/cookie-policy" className="text-ivory/50 hover:text-gold">Cookie Policy</Link>
        </div>
      </footer>
    </div>
  );
}
